// Keep the native variant values and change events; only enhance the presentation.
if (!customElements.get('pf-cro-select')) customElements.define('pf-cro-select', class extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    const listener = {signal: this.events.signal};
    this.source = this.querySelector('select');
    const label = this.querySelector('label');
    this.custom?.remove();
    this.custom = document.createElement('div');
    this.custom.className = 'pf-cro-select__custom';
    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.className = 'pf-cro-select__trigger';
    this.trigger.setAttribute('role', 'combobox');
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-labelledby', label.id);
    this.valueLabel = document.createElement('span');
    const mark = document.createElement('span');
    mark.className = 'pf-cro-select__mark';
    mark.setAttribute('aria-hidden', 'true');
    this.trigger.append(this.valueLabel, mark);
    this.list = document.createElement('ul');
    this.list.id = `${this.source.id}-list`;
    this.list.className = 'pf-cro-select__list';
    this.list.setAttribute('role', 'listbox');
    this.list.setAttribute('aria-labelledby', label.id);
    this.list.hidden = true;
    this.trigger.setAttribute('aria-controls', this.list.id);
    this.options = Array.from(this.source.options, (option, index) => {
      const item = document.createElement('li');
      item.id = `${this.list.id}-${index}`;
      item.className = 'pf-cro-select__option';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-disabled', String(option.disabled));
      item.tabIndex = -1;
      item.dataset.index = index;
      item.textContent = option.textContent;
      return item;
    });
    this.list.append(...this.options);
    this.custom.append(this.trigger, this.list);
    this.append(this.custom);
    this.source.addEventListener('change', () => this.sync(), listener);
    label.addEventListener('click', event => {event.preventDefault(); this.trigger.focus();}, listener);
    this.trigger.addEventListener('click', () => this.list.hidden ? this.open() : this.close(), listener);
    this.trigger.addEventListener('keydown', event => this.onKey(event), listener);
    this.list.addEventListener('click', event => {
      const item = event.target.closest('[role="option"]');
      if (item) this.choose(Number(item.dataset.index));
    }, listener);
    document.addEventListener('pointerdown', event => {if (!this.contains(event.target)) this.close();}, listener);
    this.addEventListener('focusout', event => {if (!this.contains(event.relatedTarget)) this.close();}, listener);
    this.sync();
    this.source.hidden = true;
  }
  disconnectedCallback() {this.events?.abort();}
  enabled() {return Array.from(this.source.options, (option, index) => option.disabled ? -1 : index).filter(index => index >= 0);}
  open() {
    this.list.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.activate(this.source.selectedIndex);
  }
  close() {
    this.list.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.removeAttribute('aria-activedescendant');
    this.search = '';
  }
  activate(index) {
    if (!this.options[index]) return;
    this.active = index;
    this.options.forEach((item, i) => item.dataset.active = String(i === index));
    const item = this.options[index];
    this.trigger.setAttribute('aria-activedescendant', item.id);
    const top = item.getBoundingClientRect().top - this.list.getBoundingClientRect().top + this.list.scrollTop - this.list.clientTop;
    if (top < this.list.scrollTop) this.list.scrollTop = top;
    else if (top + item.offsetHeight > this.list.scrollTop + this.list.clientHeight) this.list.scrollTop = top + item.offsetHeight - this.list.clientHeight;
  }
  choose(index, focus = true) {
    if (!this.source.options[index] || this.source.options[index].disabled) return;
    this.source.selectedIndex = index;
    this.source.dispatchEvent(new Event('change', {bubbles: true}));
    this.close();
    if (focus) this.trigger.focus({preventScroll: true});
  }
  onKey(event) {
    const key = event.key;
    if (key === 'Tab') {if (!this.list.hidden) {this.choose(this.active, false); this.close();} return;}
    if (key === 'Escape') {if (!this.list.hidden) {event.preventDefault(); this.close();} return;}
    if (['Enter', ' '].includes(key)) {event.preventDefault(); this.list.hidden ? this.open() : this.choose(this.active); return;}
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) {
      event.preventDefault();
      const wasClosed = this.list.hidden;
      if (wasClosed) this.open();
      const enabled = this.enabled();
      if (key === 'Home') this.activate(enabled[0]);
      else if (key === 'End') this.activate(enabled[enabled.length - 1]);
      else if (!wasClosed) {
        const next = key === 'ArrowDown' ? enabled.find(index => index > this.active) : enabled.slice().reverse().find(index => index < this.active);
        if (next !== undefined) this.activate(next);
      }
      return;
    }
    if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      if (this.list.hidden) this.open();
      this.search = Date.now() - (this.searchAt || 0) < 700 ? (this.search || '') + key : key;
      this.searchAt = Date.now();
      const normalize = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
      const index = this.enabled().find(i => normalize(this.options[i].textContent).startsWith(normalize(this.search)));
      if (index !== undefined) this.activate(index);
    }
  }
  sync() {
    this.valueLabel.textContent = this.source.selectedOptions[0]?.textContent || '';
    this.trigger.disabled = this.source.disabled;
    this.options.forEach((item, index) => item.setAttribute('aria-selected', String(index === this.source.selectedIndex)));
  }
});

if (!customElements.get('pf-cro-rose-sizes')) customElements.define('pf-cro-rose-sizes', class extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    const listener = {signal: this.events.signal};
    this.source = this.querySelector('[data-variant]');
    this.choices = Array.from(this.querySelectorAll('[data-rose-size]'));
    this.more = this.querySelector('[data-rose-more]');
    this.choices.forEach(input => input.addEventListener('change', event => {
      event.stopPropagation();
      if (!input.checked || input.disabled) return;
      this.source.value = input.value;
      this.source.dispatchEvent(new Event('change', {bubbles: true}));
    }, listener));
    this.source.addEventListener('change', () => this.sync(), listener);
    this.sync();
    this.querySelector('[data-rose-fallback]').hidden = true;
    this.querySelector('[data-rose-enhanced]').hidden = false;
  }
  disconnectedCallback() {this.events?.abort();}
  sync() {
    const option = this.source.selectedOptions[0];
    if (!option) return;
    const options = new Map(Array.from(this.source.options, item => [item.value, item]));
    let secondary = false;
    this.choices.forEach(input => {
      input.checked = input.value === option.value;
      input.disabled = options.get(input.value)?.disabled || false;
      if (input.checked && !input.hasAttribute('data-rose-recommended-size')) secondary = true;
    });
    const label = option.textContent.trim();
    this.querySelector('[data-rose-selected]').textContent = label;
    this.querySelector('[data-rose-more-selection]').textContent = secondary ? label : '';
    if (secondary) this.more.open = true;
  }
});

if (!customElements.get('pf-cro-product')) customElements.define('pf-cro-product', class extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
    this.money = new Intl.NumberFormat(this.dataset.locale, {style:'currency', currency:this.dataset.currency});
    this.variantInput = this.querySelector('[data-variant]');
    this.status = this.querySelector('[data-status]');
    this.isAmor = this.hasAttribute('data-amor');
    const requestedVariant = new URLSearchParams(location.search).get('variant');
    this.amorNeedsChoice = this.isAmor && Boolean(requestedVariant) && !this.variants.some(variant => String(variant.id) === requestedVariant);
    this.querySelectorAll('[data-amor-size]').forEach(button => button.addEventListener('click', () => {
      this.variantInput.value = button.dataset.amorSize;
      this.amorNeedsChoice = false;
      this.variantInput.dispatchEvent(new Event('change', {bubbles:true}));
    }));
    this.form = this.querySelector('[data-purchase-form]');
    this.file = this.querySelector('[data-photo]');
    this.form.addEventListener('submit', event => {event.preventDefault();this.add();});
    this.querySelectorAll('[data-add]').forEach(button => button.addEventListener('click', () => this.add()));
    this.addEventListener('change', event => {
      if (event.target.matches('[data-variant]')) {
        const variant = this.variant();
        const url = new URL(location.href);url.searchParams.set('variant',variant.id);history.replaceState({},'',url);
        if (this.isAmor) this.selectImage(this.dataset.featuredImage,'',this.dataset.featuredAlt);
        else if (variant.featured_image) this.selectImage(variant.featured_image.src,variant.featured_image.id,variant.featured_image.alt || this.dataset.title);
      }
      if (event.target.matches('[data-extra-select]')) this.option(event.target);
      if (event.target.matches('[data-variant],[data-greens]')) this.syncGreenery();
      if (event.target === this.file) this.photoPreview();
      this.sync();
    });
    this.querySelector('[data-message]').addEventListener('input', event => {this.querySelector('[data-counter]').textContent = `${event.target.value.length} / 200`;});
    this.querySelectorAll('[data-image]').forEach(button => button.addEventListener('click', () => this.selectImage(button.dataset.image,button.dataset.imageId,button.dataset.alt)));
    const initialImagePath = new URL(this.querySelector('.pf-cro-hero-image').src,location.href).pathname;
    this.querySelectorAll('[data-image]').forEach(button=>button.setAttribute('aria-pressed',String(new URL(button.dataset.image,location.href).pathname===initialImagePath)));
    this.dialog = this.querySelector('dialog');
    this.querySelector('[data-zoom]').addEventListener('click', () => this.dialog.showModal());
    this.querySelector('[data-close-zoom]').addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', event => {if(event.target === this.dialog)this.dialog.close();});
    this.querySelectorAll('[data-extra-select]').forEach(select => this.option(select));
    const offer = new URLSearchParams(location.search).get('pf_gift');
    if (['memory','complete'].includes(offer)) {
      const photo = this.querySelector('[data-kind="photo"]');
      if(photo)photo.checked=true;
      if(offer==='complete'){const vase=this.querySelector('[data-kind="vase"]');if(vase)vase.checked=true;}
      this.querySelector('[data-extras-details]').open=true;
    }
    if(this.dataset.photoIncluded==='true')this.querySelector('[data-extras-details]').open=true;
    this.querySelectorAll('[data-quick-gift]').forEach(button => button.addEventListener('click', () => {
      const kind=button.dataset.quickGift;
      const selected=this.querySelector(`[data-kind="${kind}"]:checked`);
      const input=selected||this.querySelector(`[data-kind="${kind}"]`);
      if(!input)return;
      if(kind==='photo') {
        input.checked=true;this.openGift(kind);
      } else if(selected) {
        if(kind==='vase')this.querySelector('[name="pf-cro-vase"][value=""]').checked=true;
        else input.checked=false;
      } else input.checked=true;
      this.sync();
    }));
    this.addEventListener('pf:gift-interest',event=>this.openGift(event.detail));
    const interest=new URLSearchParams(location.search).get('pf_interest');
    if(interest)this.openGift(interest,false);
    this.setupGreenery();
    this.sync();
  }
  setupGreenery() {
    this.greensInput=this.querySelector('[data-greens]');
    if(!this.greensInput)return;
    this.greenPrice=0;
    this.greenSource=this.querySelector('[data-greenery-source] form');
    this.roseMedia=JSON.parse(this.querySelector('[data-rose-media]')?.textContent||'{}');
    let attempts=0;
    const initialize=()=>{
      const yes=this.greenSource.querySelector('input[type="radio"][data-field-name="Greens"][data-addon-price]');
      const no=this.greenSource.querySelector('input[data-field-name="Greens"][original-value="No"]');
      if(yes&&no&&typeof window.GPOConfigs?.syncGpoHiddenInputs==='function'&&typeof window.GPOConfigs?.getSumAddOns==='function'){
        this.greenSource.querySelectorAll('input[type="radio"][original-value="No"]').forEach(input=>{if(!input.checked)input.click();});
        this.greensInput.disabled=false;
        this.syncGreenery();this.sync();
        return true;
      }
      if(++attempts>=60){this.querySelector('[data-greenery-status]').textContent=this.copy.greenery_error;return true;}
      return false;
    };
    if(!initialize())this.greenTimer=setInterval(()=>{if(initialize()){clearInterval(this.greenTimer);this.greenTimer=null;}},250);
  }
  syncGreenery() {
    if(!this.greensInput)return;
    this.greenValid=false;
    const yes=this.greenSource.querySelector('input[type="radio"][data-field-name="Greens"][data-addon-price]');
    const no=this.greenSource.querySelector('input[data-field-name="Greens"][original-value="No"]');
    const variant=this.greenSource.querySelector('[name="id"]');
    if(!yes||!no||!variant||typeof window.GPOConfigs?.syncGpoHiddenInputs!=='function')return;
    variant.value=this.variantInput.value;
    const input=this.greensInput.checked?yes:no;
    if(!input.checked)input.click();
    window.GPOConfigs.syncGpoHiddenInputs(this.greenSource);
    this.greenPrice=Math.round(Number(yes.getAttribute('data-addon-price'))*100);
    this.querySelector('[data-greenery-price]').textContent=Number.isFinite(this.greenPrice)?'+'+this.money.format(this.greenPrice/100):'';
    const entries=[...new FormData(this.greenSource)].filter(([key])=>key.startsWith('properties['));
    this.greenProperties=Object.fromEntries(entries.map(([key,value])=>[key.slice(11,-1),value]));
    try {
      const charges=JSON.parse(this.greenProperties._gpo_addon_products||'[]');
      const charge=charges[0];
      this.greenValid=Number.isFinite(this.greenPrice)&&this.greenPrice>0&&(!this.greensInput.checked||(
        !input.disabled&&input.checked&&this.greenProperties.Greens===input.value&&
        this.greenProperties._has_gpo&&this.greenProperties._gpo_options&&this.greenProperties._gpo_product_group&&
        charges.length===1&&String(charge.i)===this.variantInput.value&&charge.t==='price'&&Number(charge.q)===1&&
        Math.round(Number(charge.p)*100)===this.greenPrice&&Number(window.GPOConfigs.getSumAddOns())===this.greenPrice
      ));
    } catch(_){this.greenValid=false;}
    this.querySelector('[data-greenery-status]').textContent=this.greenValid?'':this.copy.greenery_error;
    const image=this.roseMedia[`${parseInt(this.variant().option1,10)}-${this.greensInput.checked?'eucalyptus':'plain'}`];
    if(image)this.selectImage(image.url,image.id,image.alt);
  }
  greeneryProperties() {
    if(!this.greensInput?.checked)return {};
    this.syncGreenery();
    if(!this.greenValid)throw new Error(this.copy.greenery_error);
    const pricing=Object.fromEntries(Object.entries(this.greenProperties).filter(([key])=>key.startsWith('_')));
    const selected={[this.greensInput.dataset.label]:this.greensInput.dataset.yes};
    const options=JSON.parse(pricing._gpo_options);
    const internal=Object.fromEntries(Object.entries(options).filter(([key])=>key.startsWith('_')));
    pricing._gpo_options=JSON.stringify({...internal,...selected});
    return {...pricing,...selected,_pf_cro_greenery_price:String(this.greenPrice)};
  }
  async removeUnverifiedGroup(group) {
    for(let attempt=0;attempt<10;attempt++){
      const response=await fetch(this.route('cart.js'),{cache:'no-store'});
      if(!response.ok)throw new Error(this.copy.cart_check);
      const cart=await response.json();
      const item=cart.items.find(item=>item.properties?._pf_cro_group===group);
      if(!item)return;
      const removal=await fetch(this.route('cart/change.js'),{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:item.key,quantity:0})});
      if(!removal.ok)throw new Error(this.copy.cart_check);
    }
    throw new Error(this.copy.cart_check);
  }
  openGift(kind,scroll=true) {
    const input=this.querySelector(`[data-kind="${kind}"]`);
    const details=this.querySelector('[data-extras-details]');
    details.open=true;
    if(scroll)(input?.closest('.pf-cro-extra-group,.pf-cro-extra-row')||details).scrollIntoView({block:'center',behavior:'auto'});
  }
  disconnectedCallback() {if(this.previewURL)URL.revokeObjectURL(this.previewURL);clearInterval(this.greenTimer);}
  variant() {return this.variants.find(variant => String(variant.id)===this.variantInput.value);}
  selected() {return [...this.querySelectorAll('[data-extra]:checked')];}
  option(select) {
    const option=select.selectedOptions[0];
    if(!option)return;
    const input=this.querySelector(`[data-kind="${select.dataset.extraSelect}"]`);
    input.value=option.value;input.dataset.price=option.dataset.price;input.dataset.variantTitle=option.textContent.split(' · ')[0];
    this.querySelector(`[data-selected-price="${select.dataset.extraSelect}"]`).textContent='+'+this.money.format(Number(option.dataset.price)/100);
    if(select.dataset.extraSelect==='plush'&&option.dataset.image)this.querySelector('[data-plush-image]').src=option.dataset.image;
  }
  selectImage(src,id,alt) {
    const image=this.querySelector('.pf-cro-hero-image');
    const sixNote=this.querySelector('[data-six-photo-note]');
    if(sixNote)sixNote.hidden=new URL(src,location.href).pathname!==new URL(this.dataset.featuredImage,location.href).pathname;
    image.removeAttribute('srcset');image.src=src;image.alt=alt||this.dataset.title;
    this.dialog.querySelector('img').src=src;this.dialog.querySelector('img').alt=image.alt;
    this.querySelectorAll('[data-image]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.imageId===String(id)||new URL(button.dataset.image,location.href).pathname===new URL(src,location.href).pathname)));
  }
  photoSelected() {return this.dataset.photoIncluded==='true'||Boolean(this.querySelector('[data-kind="photo"]')?.checked);}
  photoPreview() {
    if(this.previewURL)URL.revokeObjectURL(this.previewURL);
    const preview=this.querySelector('[data-photo-preview]');
    const file=this.file.files[0];
    preview.hidden=!file;
    if(file){this.previewURL=URL.createObjectURL(file);preview.src=this.previewURL;}
  }
  sync() {
    const variant=this.variant();
    const selected=this.selected();
    if(this.isAmor){
      const count = String(variant.option1).split(' ')[0];
      const format = text => text.replaceAll('__COUNT__',count);
      this.querySelector('[data-amor-subtitle]').textContent=format(this.copy.amor_subtitle);
      this.querySelector('[data-amor-summary]').textContent=format(this.copy.amor_summary);
      this.querySelector('[data-amor-retired]').hidden=!this.amorNeedsChoice;
      this.querySelectorAll('[data-amor-size]').forEach(button=>button.setAttribute('aria-pressed',String(!this.amorNeedsChoice&&button.dataset.amorSize===String(variant.id))));
    }
    const greenTotal=this.greensInput?.checked?(this.greenPrice||0):0;
    const extraTotal=selected.reduce((total,input)=>total+Number(input.dataset.price),greenTotal);
    this.querySelectorAll('[data-base-price]').forEach(node=>node.textContent=this.money.format(variant.price/100));
    this.querySelectorAll('[data-total]').forEach(node=>node.textContent=this.money.format((variant.price+extraTotal+Number(this.dataset.delivery))/100));
    this.querySelector('[data-extras-price]').textContent=this.money.format(extraTotal/100);
    this.querySelector('[data-extras-line]').hidden=!selected.length&&!greenTotal;
    this.querySelector('[data-extras-summary]').textContent=[...(greenTotal?[this.greensInput.dataset.label]:[]),...selected.map(input=>input.dataset.label)].join(' · ')||this.copy.extras_hint;
    if(this.file){this.querySelector('[data-photo-fields]').hidden=!this.photoSelected();this.file.required=this.photoSelected();}
    this.querySelectorAll('[data-quick-gift]').forEach(button=>{
      const selected=this.querySelector(`[data-kind="${button.dataset.quickGift}"]:checked`);
      button.setAttribute('aria-pressed',String(Boolean(selected)));
      button.textContent=selected?button.dataset.addedLabel:button.dataset.defaultLabel;
      if(button.dataset.quickGift==='vase'){
        const source=selected||this.querySelector('[data-kind="vase"]');
        const card=button.closest('article');
        const row=source?.closest('label');
        if(row){card.querySelector('img').src=row.querySelector('img').src;card.querySelector('[data-quick-title]').textContent=source.dataset.label;card.querySelector('[data-quick-price]').textContent='+'+this.money.format(Number(source.dataset.price)/100);}
      }
    });
    this.querySelectorAll('[data-add]').forEach(button=>{button.disabled=this.busy||this.amorNeedsChoice||!variant.available||Boolean(this.greensInput?.checked&&!this.greenValid);button.textContent=this.busy?this.copy.adding:this.amorNeedsChoice?this.copy.amor_choose:variant.available?this.copy.add:this.copy.sold_out;});
  }
  route(path) {return (this.dataset.root.endsWith('/')?this.dataset.root:this.dataset.root+'/')+path;}
  cartURL() {const url=new URL(this.route('cart'),location.origin);const preview=new URLSearchParams(location.search).get('preview_theme_id');if(preview)url.searchParams.set('preview_theme_id',preview);return url.href;}
  async add() {
    if(this.busy||this.amorNeedsChoice||!this.variant().available)return;
    this.status.textContent='';this.querySelector('[data-review-cart]').hidden=true;
    const file=this.file?.files[0];
    if(this.photoSelected()&&(!file||!['image/jpeg','image/png','image/webp'].includes(file.type)||file.size>10*1024*1024)){
      this.querySelector('[data-extras-details]').open=true;this.status.textContent=this.copy.photo_error;this.file?.focus();return;
    }
    if(!this.form.reportValidity())return;
    let pricing={};
    try{pricing=this.greeneryProperties();}catch(error){this.status.textContent=error.message;this.sync();return;}
    this.busy=true;this.sync();
    const group=crypto.randomUUID();
    const variant=this.variant();
    const properties={...pricing,'_pf_cro_group':group};
    const message=this.querySelector('[data-message]').value.trim();
    const signature=this.querySelector('[data-signature]').value.trim();
    const recipient=this.querySelector('[data-recipient]').value.trim();
    if(message)properties[this.copy.properties.message]=message;
    if(signature)properties[this.copy.properties.from]=signature;
    if(recipient)properties[this.copy.properties.to]=recipient;
    const items=[{id:variant.id,quantity:1,properties},...this.selected().map(input=>({id:Number(input.value),quantity:1,properties:{'_pf_cro_group':group,[this.copy.properties.to]:this.dataset.title+(variant.title!=='Default Title'?' · '+variant.title:'')}}))];
    const body=new FormData();
    items.forEach((item,index)=>{body.append(`items[${index}][id]`,item.id);body.append(`items[${index}][quantity]`,item.quantity);Object.entries(item.properties).forEach(([key,value])=>body.append(`items[${index}][properties][${key}]`,value));});
    if(this.photoSelected())body.append(`items[0][properties][${this.copy.properties.photo}]`,file,file.name);
    try {
      const response=await fetch(this.route('cart/add.js'),{method:'POST',headers:{Accept:'application/json'},body});
      const result=await response.json();
      if(!response.ok||result.status)throw new Error(result.description||this.copy.error);
      let added=result.items||[];
      const hasSelection=lines=>items.every(item=>lines.some(line=>String(line.variant_id||line.id)===String(item.id)&&line.quantity===1));
      if(!hasSelection(added)) {
        const cartResponse=await fetch(this.route('cart.js'),{cache:'no-store',headers:{Accept:'application/json'}});
        if(!cartResponse.ok)throw new Error(this.copy.cart_check);
        const cart=await cartResponse.json();
        added=cart.items.filter(item=>item.properties?._pf_cro_group===group);
        if(!hasSelection(added))throw new Error(this.copy.cart_check);
      }
      if(this.photoSelected()&&!/^https:\/\//.test(added.find(line=>String(line.variant_id||line.id)===String(variant.id))?.properties?.[this.copy.properties.photo]||''))throw new Error(this.copy.cart_check);
      if(pricing._pf_cro_greenery_price){
        const parent=added.find(line=>String(line.variant_id||line.id)===String(variant.id));
        if(Number(parent?.price)!==variant.price+Number(pricing._pf_cro_greenery_price)){
          await this.removeUnverifiedGroup(group);
          throw new Error(this.copy.greenery_error);
        }
      }
      this.status.textContent=this.copy.added;
      location.assign(this.cartURL());
    } catch(error) {
      let found=false;
      try{const response=await fetch(this.route('cart.js')+'?pf_check='+encodeURIComponent(group),{cache:'no-store'});const cart=await response.json();found=cart.items.some(item=>item.properties?._pf_cro_group===group);}catch(_){found=true;}
      this.status.textContent=found?this.copy.cart_check:(error.message||this.copy.error);
      const link=this.querySelector('[data-review-cart]');link.href=this.cartURL();link.hidden=false;
      this.busy=found;this.sync();
      if(found)this.querySelectorAll('[data-add]').forEach(button=>{button.disabled=true;button.textContent=this.copy.cart_check;});
      this.status.scrollIntoView({block:'center',behavior:'auto'});
    }
  }
});
